/*
 * Copyright (c) 2023 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, March 21st 2023, 4:29:46 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import CategoryIcon from "@mui/icons-material/Category";
import {
  APIMany2OneSelectFormInput,
  APIMany2OneEditButton,
  ConcreteAPIMany2OneSelectFormInputProps,
  CheckBoxFormInput,
} from "ui-widgets";
import { ProcessTemplateCategory } from "lib/schemas";
import { APISERVICEPATHS } from "lib/api";
import { useOSFormContext } from "ui-widgets/forms/providers";
import { useProcessCategoryAPI } from "service-builder/services";
import { TranslatableTextFormInput } from "translation/components";
import { OSAPIForm, StyledOSDialog, useToggleState, FormSubmitBubblingStopper } from "ui-widgets";

interface CategoryPickerProps<TPrimaryObject>
  extends ConcreteAPIMany2OneSelectFormInputProps<TPrimaryObject, ProcessTemplateCategory>,
    Pick<React.ComponentProps<typeof ProcessCategoryForm>, "organizationId" | "site"> {}

export const ProcessCategoryPicker = <TPrimaryObject,>(props: CategoryPickerProps<TPrimaryObject>) => {
  const { organizationId, site } = props;
  const { categoryId, closeEdit, editFormOpened, openEdit } = useProcessCategoryPicker(props.schema_field_key);

  return (
    <FormSubmitBubblingStopper>
      <APIMany2OneSelectFormInput<TPrimaryObject, ProcessTemplateCategory>
        label={props.label ?? "Category"}
        itemIdProperty="id"
        getOptionLabel={(option) => option.title}
        loadingText="Loading Categories"
        apiServicePath={APISERVICEPATHS.processTemplateCategories}
        inputStartAdornment={<CategoryIcon color="primary" />}
        inputEndAdornment={<APIMany2OneEditButton objectId={categoryId} onClick={openEdit} />}
        renderRelatedObjectForm={({ closeNewFormFn, newFormOpenState, updatePrimaryObject }) => (
          <ProcessCategoryForm
            onClose={closeNewFormFn}
            opened={newFormOpenState}
            organizationId={organizationId}
            site={site}
            onSave={(category) => updatePrimaryObject(category.id, false)}
          />
        )}
        {...props}
      />
      {editFormOpened && (
        <ProcessCategoryForm
          onClose={closeEdit}
          opened={editFormOpened}
          organizationId={organizationId}
          site={site}
          categoryId={categoryId}
        />
      )}
    </FormSubmitBubblingStopper>
  );
};

const useProcessCategoryPicker = <TPrimaryObject,>(
  schema_field_key: ConcreteAPIMany2OneSelectFormInputProps<TPrimaryObject, ProcessTemplateCategory>["schema_field_key"]
) => {
  const { watch } = useOSFormContext<TPrimaryObject>();

  const categoryId = watch<any>(schema_field_key) as ProcessTemplateCategory["id"];

  const { handleClose: closeEdit, handleOpen: openEdit, open: editFormOpened } = useToggleState();

  return { categoryId, openEdit, closeEdit, editFormOpened };
};

interface ProcessCategoryFormProps {
  organizationId: ProcessTemplateCategory["artifact_org"];
  site: ProcessTemplateCategory["site"];
  categoryId?: ProcessTemplateCategory["id"];
  opened: boolean;
  onClose: () => void;
  onSave?: (category: ProcessTemplateCategory) => void;
}

const ProcessCategoryForm = ({
  organizationId,
  site,
  categoryId,
  opened,
  onClose,
  onSave,
}: ProcessCategoryFormProps) => {
  const { useProcessCategoryAPIForm } = useProcessCategoryAPI();
  const { useAPIFormReturn } = useProcessCategoryAPIForm({
    categoryId,
    organizationId,
    site,
    handleAPIResponse: onSave,
  });

  return (
    <StyledOSDialog open={opened} onClose={onClose} popUpTitle="Category" popUpIcon={<CategoryIcon color="primary" />}>
      <OSAPIForm<ProcessTemplateCategory> useAPIFormReturn={useAPIFormReturn}>
        <TranslatableTextFormInput<ProcessTemplateCategory> schema_field_key="title" label="Title" />
        <CheckBoxFormInput
          schema_field_key="extra_configurations.exclude_from_services_menu"
          label="Exclude from services menu"
        />
      </OSAPIForm>
    </StyledOSDialog>
  );
};
